import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../components/common/SafeIcon';
import BrandVideoPlayer from '../components/training/BrandVideoPlayer';
import SessionChaptersPanel from '../components/training/SessionChaptersPanel';
import SessionLifecycleActions from '../components/training/SessionLifecycleActions';
import SessionDetails from '../components/training/SessionDetails';
import {
  checkCanEditSessionChapters,
  checkIsAdminOrHigher,
  fetchSessionById,
  upsertSessionCompletion,
} from '../services/training';
import { normalizeChapters } from '../utils/videoTime';

function calcPercent(currentTime, duration) {
  if (!duration || duration <= 0) return 0;
  return Math.min(100, Math.round((currentTime / duration) * 100));
}

export default function TrainingSessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const lastSavedPercent = useRef(0);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(Boolean(location.state?.uploadSuccess));
  const [canEditChapters, setCanEditChapters] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!location.state?.uploadSuccess) return undefined;
    const timer = setTimeout(() => setShowSuccess(false), 6000);
    return () => clearTimeout(timer);
  }, [location.state?.uploadSuccess]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetchSessionById(id)
      .then(async (data) => {
        if (!active) return;
        if (data?.isDraft) {
          navigate(`/app/entrenamientos/${data.id}/borrador`, { replace: true });
          return;
        }
        setSession(data);
        lastSavedPercent.current = 0;
        setCurrentTime(0);
        if (data?.uploadedBy) {
          try {
            const [allowed, admin] = await Promise.all([
              checkCanEditSessionChapters(data.uploadedBy),
              checkIsAdminOrHigher(),
            ]);
            if (active) {
              setCanEditChapters(allowed);
              setIsAdmin(admin);
            }
          } catch {
            if (active) {
              setCanEditChapters(false);
              setIsAdmin(false);
            }
          }
        } else if (active) {
          setCanEditChapters(false);
          setIsAdmin(false);
        }
      })
      .catch(console.error)
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [id, navigate]);

  const saveProgress = useCallback((watched, percentWatched) => {
    if (!session?.id) return;
    const rounded = Math.min(100, Math.max(0, Math.round(percentWatched)));
    if (!watched && rounded <= lastSavedPercent.current && rounded > 0) return;
    lastSavedPercent.current = rounded;
    upsertSessionCompletion(session.id, { watched, percentWatched: rounded }).catch(console.error);
  }, [session?.id]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !session?.id) return;
    setCurrentTime(video.currentTime);
    const percent = calcPercent(video.currentTime, video.duration);
    if (percent >= 5 && percent - lastSavedPercent.current >= 5) {
      saveProgress(false, percent);
    }
  };

  const handleChaptersUpdated = (chapters) => {
    setSession((prev) => (prev ? { ...prev, chapters } : prev));
  };

  const handleMetadataUpdated = (updated) => {
    setSession(updated);
  };

  const handleSessionUpdated = async () => {
    const data = await fetchSessionById(id);
    if (data) setSession(data);
  };

  const handleEnded = () => {
    saveProgress(true, 100);
  };

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video || !session?.id) return;
    const percent = calcPercent(video.currentTime, video.duration);
    if (percent > 0) {
      saveProgress(false, Math.max(percent, 1));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando sesión…
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex flex-col items-center justify-center gap-4 px-8">
        <p className="text-[var(--text-main)] font-black uppercase">Sesión no encontrada</p>
        <button
          type="button"
          onClick={() => navigate('/app/entrenamientos')}
          className="text-brand-cyan text-sm font-bold uppercase tracking-widest"
        >
          Volver a Entrenamientos
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-20 md:pb-0 transition-colors duration-300">
      <div className="sticky top-0 z-20 bg-[var(--bg-base)]/90 backdrop-blur-md p-4 flex items-center gap-4 border-b border-[var(--border-subtle)]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] hover:bg-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] transition-colors border border-[var(--border-subtle)]"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </button>
        <span className="font-bold text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)] truncate">Volver a Entrenamientos</span>
      </div>

      {showSuccess && (
        <div className="mx-4 mt-4 p-4 rounded-xl border border-brand-green/40 bg-brand-green/10 text-[var(--text-main)] text-sm flex items-center gap-3">
          <SafeIcon name="CheckCircle" className="w-5 h-5 text-brand-green flex-shrink-0" />
          <span>Tu sesión ya está publicada y visible para todos los miembros.</span>
        </div>
      )}

      {session.isArchived && canEditChapters && (
        <div className="mx-4 mt-4 p-4 rounded-xl border border-amber-400/40 bg-amber-500/10 text-[var(--text-main)] text-sm flex items-center gap-3">
          <SafeIcon name="Archive" className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <span>Esta sesión está archivada y oculta para los miembros.</span>
        </div>
      )}

      <div className="w-full bg-black aspect-video">
        {session.videoUrl ? (
          <BrandVideoPlayer
            ref={videoRef}
            key={session.videoUrl}
            src={session.videoUrl}
            poster={session.thumbnail}
            className="w-full h-full"
            videoClassName="w-full h-full"
            objectFit="contain"
            chapters={normalizeChapters(session.chapters)}
            onPlay={handlePlay}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
          />
        ) : (
          <div className="relative w-full h-full min-h-[200px] flex flex-col items-center justify-center gap-3 px-8 text-center bg-black">
            <img
              src={session.thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
            />
            <p className="relative text-sm font-bold text-white/90 max-w-md">
              El video de esta sesión no está disponible todavía.
            </p>
            <p className="relative text-xs text-white/60 max-w-md">
              Las sesiones subidas antes de esta actualización necesitan que un admin complete la URL de reproducción en Publit.io, o volver a publicar la sesión.
            </p>
          </div>
        )}
      </div>

      {(session.chapters?.length > 0 || canEditChapters) && (
        <SessionChaptersPanel
          sessionId={session.id}
          chapters={session.chapters}
          durationSeconds={session.durationSeconds}
          canEdit={canEditChapters}
          videoRef={videoRef}
          currentTime={currentTime}
          onChaptersUpdated={handleChaptersUpdated}
        />
      )}

      <SessionDetails
        session={session}
        videoRef={videoRef}
        canEdit={canEditChapters}
        isAdmin={isAdmin}
        onMetadataUpdated={handleMetadataUpdated}
      />

      <SessionLifecycleActions
        session={session}
        canEdit={canEditChapters}
        isAdmin={isAdmin}
        onSessionUpdated={handleSessionUpdated}
      />
    </div>
  );
}
